/**
 * DUERP-MAPPING.UTILS
 * Lecture brute du document unique -> parametres du compte, sans assistant.
 * Familles et attributs se rapprochent par leur nom ; un barème se convertit niveau par niveau,
 * au prorata. Ce qui ne correspond pas est soumis a l'utilisateur (EstablishmentImportSettingsModal).
 */
import { AccountRisksSettings, getScale, RiskAttribute } from "@/redux/account.types"
import { RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import { sortScaleItems } from "@/entities/physical-risk/utils/sortScales"

export const SCALES = ["severity", "frequency", "prevention"] as const
export type ScaleKey = (typeof SCALES)[number]

//Forme renvoyee par la lecture du document (cf. duerp-reading.utils)
interface ImportedPreventionAction {
  name: string
  description: string
  status: "todo" | "pending" | "done" | null
  dateStarted: string | null
  datePlanned: string | null
  dateDone: string | null
  cost: number | null
  financedBy: string | null
}

interface ImportedRisk {
  //Lignes du document d'ou vient le risque ("onglet!ligne")
  sources?: string[]
  family: string | null
  RiskFamilyId: string | null
  //Cle = id de l'attribut du compte
  attributes: Record<string, string>
  //Scores du barème du compte ; null dans la lecture brute quand le document n'en donne pas
  severity: number | null
  frequency: number | null
  prevention: number | null
  //Ids des unites de travail de ImportedDocument
  units: string[]
  preventionActions: ImportedPreventionAction[]
}

export interface ImportedDocument {
  //Ni fonctions ni lieux : leur type est choisi a l'import
  units: { id: string; name: string; map: string | null }[]
  risks: ImportedRisk[]
}

/**
 * Lecture brute : meme forme que la relecture, mais dans les termes du document. La famille est son
 * nom dans le document, les attributs sont indexes par les en-tetes de colonnes, les scores sont ceux
 * de son barème. "scales" : la legende de cotation du document, quand il la donne.
 */
export interface RawDocument extends ImportedDocument {
  scales: Partial<Record<ScaleKey, { value: number; label: string; description?: string }[]>>
}

/**
 * Choix de correspondance. Une famille : id du compte, null = "Non défini". Un attribut : id du compte,
 * null = ignore. Un score : valeur du barème du compte.
 */
export interface DuerpMapping {
  families: Record<string, string | null>
  attributes: Record<string, string | null>
  scales: Record<ScaleKey, Record<string, number>>
}

//Nom compare sans casse, accents, ponctuation ni pluriel : "Chutes de plain pied" = "Chute de plain-pied"
export function matchKey(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.replace(/s$/, ""))
    .join(" ")
}

// Une colonne désignée comme famille fournit le rattachement de chaque risque à l'import.
export function withFamilyColumn(raw: RawDocument, column: string | undefined): RawDocument {
  if (!column) return raw
  return { ...raw, risks: raw.risks.map((risk) => ({
    ...risk, family: risk.attributes?.[column]?.trim() || null
  })) }
}

//Une unite sans risque n'a rien a relire ni a importer
export function withoutUnusedUnits<T extends { units: { id: string }[]; risks: { units: string[] }[] }>(document: T): T {
  return { ...document, units: document.units.filter((unit) => document.risks.some((risk) => risk.units.includes(unit.id))) }
}

//Familles telles qu'ecrites dans le document ; "Non défini" n'est pas une famille a rapprocher
export function getDocumentFamilies(raw: RawDocument) {
  const names = raw.risks.map((risk) => risk.family?.trim()).filter((name): name is string => !!name)
  return Array.from(new Set(names)).filter((name) => matchKey(name) !== matchKey("Non défini"))
}

export function getDocumentAttributes(raw: RawDocument) {
  return Array.from(new Set(raw.risks.flatMap((risk) => Object.keys(risk.attributes ?? {}))))
}

/**
 * Niveaux du barème du document : sa legende quand il en a une, sinon les valeurs lues dans ses risques.
 * Un niveau a 0 ("non applicable") n'en est pas un : comme un score absent, il prend le niveau le plus
 * bas du compte.
 */
// Un coefficient représente un seul niveau, même si la lecture le répète sur plusieurs lignes.
export function normalizeDocumentScale(items: { value: number; label: string; description?: string }[]) {
  const levels = new Map<number, { value: number; label: string; description?: string }>()
  for (const item of items) {
    const value = Number(String(item?.value ?? "").trim().replace(",", "."))
    if (!Number.isFinite(value) || value <= 0) continue
    const previous = levels.get(value)
    const label = String(item?.label ?? "").trim()
    const description = String(item?.description ?? "").trim()
    levels.set(value, {
      value,
      label: previous?.label || label,
      description: previous?.description || (matchKey(description) === matchKey(label) ? "" : description)
    })
  }
  return [...levels.values()].sort((a, b) => a.value - b.value)
}

export function getDocumentScale(raw: RawDocument, scale: ScaleKey) {
  // La légende définit les niveaux autorisés. Les cotations des risques ne la complètent pas :
  // une colonne mal identifiée ne doit pas injecter les gravités dans la fréquence, par exemple.
  const legend = normalizeDocumentScale(raw.scales?.[scale] ?? [])
  return legend.length ? legend : normalizeDocumentScale(
    raw.risks.map((risk) => ({ value: risk[scale] ?? 0, label: "" }))
  )
}

export function getAccountScale(settings: AccountRisksSettings, scale: ScaleKey) {
  return sortScaleItems(getScale(settings[`${scale}Scale`]))
}

/**
 * Valeur du compte pour chaque niveau du document : la meme valeur quand le compte l'a, sinon le
 * niveau de meme rang au prorata (4e niveau sur 4 -> dernier niveau du compte).
 */
export function getScaleMapping(items: { value: number }[], accountScale: PhysicalRiskScaleItem[]) {
  const isSame = isSameScale(items, accountScale)
  return Object.fromEntries(
    items.map((item, index) => {
      const rank = items.length > 1 ? Math.round((index * (accountScale.length - 1)) / (items.length - 1)) : 0
      return [String(item.value), isSame ? item.value : (accountScale[rank]?.value ?? 1)]
    })
  )
}

export function getInitialMapping(
  raw: RawDocument,
  settings: AccountRisksSettings,
  families: RiskFamily[],
  attributes: RiskAttribute[]
): DuerpMapping {
  const exactAttributes = new Set(
    getDocumentAttributes(raw).flatMap(
      (label) => attributes.find((attribute) => matchKey(attribute.name) === matchKey(label))?.id ?? []
    )
  )
  //Colonne sans attribut de meme nom : l'attribut du compte de meme position, s'il est libre
  const attributesMapping = Object.fromEntries(
    getDocumentAttributes(raw).map((label, index) => {
      const exact = attributes.find((attribute) => matchKey(attribute.name) === matchKey(label))
      const byPosition = attributes[index] && !exactAttributes.has(attributes[index].id) ? attributes[index] : null
      return [label, (exact ?? byPosition)?.id ?? null]
    })
  )

  return {
    families: Object.fromEntries(
      getDocumentFamilies(raw).map((name) => [
        name,
        families.find((family) => matchKey(family.name) === matchKey(name))?.id ?? null
      ])
    ),
    attributes: attributesMapping,
    scales: Object.fromEntries(
      SCALES.map((scale) => [
        scale,
        getScaleMapping(getDocumentScale(raw, scale), getAccountScale(settings, scale))
      ])
    ) as DuerpMapping["scales"]
  }
}

//Barème du document deja dans le compte : memes valeurs, niveau pour niveau
export function isSameScale(items: { value: number }[], accountScale: PhysicalRiskScaleItem[]) {
  return items.every((item) => accountScale.some((level) => level.value === item.value))
}

/** Ecarts a soumettre a l'utilisateur ; aucun ecart = la relecture s'ouvre directement */
export function getGaps(raw: RawDocument, families: RiskFamily[], attributes: RiskAttribute[]) {
  return {
    families: getDocumentFamilies(raw).filter(
      (name) => !families.some((family) => matchKey(family.name) === matchKey(name))
    ),
    attributes: getDocumentAttributes(raw).filter(
      (label) => !attributes.some((attribute) => matchKey(attribute.name) === matchKey(label))
    ),
    //Barème du document toujours soumis, meme identique a celui du compte : l'utilisateur le verifie
    scales: SCALES.filter((scale) => getDocumentScale(raw, scale).length)
  }
}

export function hasGaps(gaps: ReturnType<typeof getGaps>) {
  return !!(gaps.families.length || gaps.attributes.length || gaps.scales.length)
}

/** Lecture brute + correspondances -> format de la relecture, dans les termes du compte */
export function applyMapping(
  raw: RawDocument,
  mapping: DuerpMapping,
  settings: AccountRisksSettings,
  attributes: RiskAttribute[]
): ImportedDocument {
  const { scales, ...document } = raw
  return {
    ...document,
    risks: raw.risks.map((risk) => {
      const RiskFamilyId = risk.family ? (mapping.families[risk.family.trim()] ?? null) : null
      //Score absent : bas de l'echelle du compte
      const score = (scale: ScaleKey) =>
        mapping.scales[scale][String(risk[scale])] ?? getAccountScale(settings, scale)[0]?.value ?? 1
      return {
        ...risk,
        RiskFamilyId,
        severity: score("severity"),
        frequency: score("frequency"),
        prevention: score("prevention"),
        //Plusieurs colonnes vers le meme attribut : leurs textes se suivent
        attributes: Object.fromEntries(
          attributes.map((attribute) => [
            attribute.id,
            Object.entries(risk.attributes ?? {})
              .filter(([label, value]) => mapping.attributes[label] === attribute.id && value?.trim())
              .map(([, value]) => value.trim())
              .join("\n")
          ])
        )
      }
    })
  }
}
